import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import cx from 'classnames';
import { I18n } from 'react-redux-i18n';

import { MessageBox } from '@common/components/MessageBox';
import ActionsButton, { TRIGGER_TYPE } from '@common/components/ActionsButton';
import { getErrorText } from '@common/utils/rest';

import { isParentOfChild, getItem, getAllChildren } from './Tree.utils';
import { TreeActionType, TreeSelectionType } from "./tree.types";
import { TreeItem } from './components/tree-item';

import './Tree.styles.scss';

export default class Tree extends Component {
    static propTypes = {
        items: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                name: PropTypes.node.isRequired,
                children: PropTypes.array,
            })
        ),
        defaultCollapsed: PropTypes.bool,
        preserveCollapsedState: PropTypes.bool,
    };

    static defaultProps = {
        normalizeId: Number,
        selectionType: TreeSelectionType.independent,
        items: [],
        itemSelectedImgSrc: 'gif/folder_open.gif',
        itemNotSelectedImgSrc: 'gif/folder_closed.gif',
        features: {},
        labels: {},
        shouldDisableItemOnMove: () => false,
        shouldDisableItem: () => false,
        onRename: () => {},
        onDelete: () => {},
        onCreate: () => {},
        onMove: () => {},
        onChange: () => {},
        getActions: () => [],
        defaultCollapsed: true,
        preserveCollapsedState: true,
    };

    state = {
        collapsedItems: [],
        activeActionsItem: null,
        renamingItem: {
            id: null,
            name: '',
        },
        movingItemId: null,
        parentIdOfCreatingItemId: null,
        messageProps: {
            header: 'Delete',
            text: '',
            show: false,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            size: 'medium',
            onCustomOk: () => {},
        },
    };

    creatingItemMetadata = {
        id: null,
        hasErrorOnCreate: false,
    };

    componentDidMount() {
        this.processCollapsedItems();
    }

    componentDidUpdate(prevProps) {
        if (this.props.collapsedItems !== prevProps.collapsedItems) {
            this.setState((state) => ({ ...state, collapsedItems: this.props.collapsedItems }));
        }
        
        // If items changed, preserve existing collapsed state and only add new items as collapsed if defaultCollapsed is true
        if (this.props.items !== prevProps.items) {
            this.preserveCollapsedStateOnItemsChange(prevProps.items);
        } else if (this.props.defaultCollapsed !== prevProps.defaultCollapsed) {
            this.processCollapsedItems();
        }
    }

    preserveCollapsedStateOnItemsChange = (prevItems) => {
        const { defaultCollapsed, preserveCollapsedState, items, normalizeId, onCollapsedItemsChange, collapsedItems: propCollapsedItems } = this.props;
        
        // If collapsedItems prop is provided, use it instead of processing
        if (propCollapsedItems !== undefined) {
            this.setState((x) => ({ ...x, collapsedItems: propCollapsedItems }));
            return;
        }

        // When collapsing is disabled there is no expand control, so nothing may stay collapsed
        if (!this.features.collapsing) {
            this.setState((x) => ({ ...x, collapsedItems: [] }));

            if (onCollapsedItemsChange) {
                onCollapsedItemsChange([]);
            }

            return;
        }

        // If preserveCollapsedState is false, just reprocess normally
        if (!preserveCollapsedState) {
            this.processCollapsedItems();
            return;
        }
        
        // Get all item IDs from previous items
        const getAllItemIds = (itemList) => {
            const ids = [];
            itemList.forEach(item => {
                ids.push(normalizeId(item.id));
                if (item.children && item.children.length > 0) {
                    ids.push(...getAllItemIds(item.children));
                }
            });
            return ids;
        };
        
        const prevItemIds = getAllItemIds(prevItems || []);
        const currentItemIds = getAllItemIds(items);
        
        // Preserve collapsed state for existing items
        const preservedCollapsedItems = this.state.collapsedItems.filter(id => 
            prevItemIds.includes(id) && currentItemIds.includes(id)
        );
        
        // Add new items as collapsed if defaultCollapsed is true
        const newItemIds = currentItemIds.filter(id => !prevItemIds.includes(id));
        const newCollapsedItems = defaultCollapsed ? newItemIds : [];
        
        const finalCollapsedItems = [...preservedCollapsedItems, ...newCollapsedItems];
        
        this.setState((x) => ({ ...x, collapsedItems: finalCollapsedItems }));
        
        if (onCollapsedItemsChange) {
            onCollapsedItemsChange(finalCollapsedItems);
        }
    }

    processCollapsedItems = () => {
        const { collapseAfterLevel, defaultCollapsed, items, normalizeId, onCollapsedItemsChange, collapsedItems: propCollapsedItems } = this.props;
        
        // If collapsedItems prop is provided, use it instead of processing
        if (propCollapsedItems !== undefined) {
            this.setState((x) => ({ ...x, collapsedItems: propCollapsedItems }));
            return;
        }

        // When collapsing is disabled there is no expand control, so nothing may start collapsed
        if (!this.features.collapsing) {
            this.setState((x) => ({ ...x, collapsedItems: [] }));

            if (onCollapsedItemsChange) {
                onCollapsedItemsChange([]);
            }

            return;
        }

        const collapsedItems = [];

        // If defaultCollapsed is true, collapse ALL items
        if (defaultCollapsed) {
            const getAllItemIds = (itemList) => {
                const ids = [];
                itemList.forEach(item => {
                    ids.push(normalizeId(item.id));
                    if (item.children && item.children.length > 0) {
                        ids.push(...getAllItemIds(item.children));
                    }
                });
                return ids;
            };
            collapsedItems.push(...getAllItemIds(items));
        } else if (collapseAfterLevel !== undefined) {
            // Original collapseAfterLevel logic
            const processItem = (item, depth) => {
                if (depth > collapseAfterLevel) {
                    collapsedItems.push(normalizeId(item.id));
                }

                item.children?.map((x) => {
                    processItem(x, depth + 1);
                });
            };

            items.forEach((x) => {
                processItem(x, 1);
            });
        }

        this.setState((x) => ({ ...x, collapsedItems }));

        if (onCollapsedItemsChange) {
            onCollapsedItemsChange(collapsedItems);
        }
    }

    setMessageProps = (props) => {
        this.setState((state) => ({
            ...state,
            messageProps: {
                size: 'medium',
                ...props,
            },
        }));
    }

    closeMessage = () => {
        this.setMessageProps({ show: false });
    }

    openErrorMessage = (text, props = {}) => {
        this.setMessageProps({
            show: true,
            confirmText: '',
            header: '',
            cancelText: 'Cancel',
            text: getErrorText(text),
            ...props
        });
    }

    shouldDisableItemOnMove = (item) => {
        const { shouldDisableItemOnMove, items } = this.props;
        const { activeActionsItem } = this.state;
        const checks = [
            () => {
                return activeActionsItem.id == item.id;
            },
            () => {
                return isParentOfChild(item, activeActionsItem);
            },
            () => {
                return isParentOfChild(activeActionsItem, item);
            },
            () => shouldDisableItemOnMove({
                currentMovingItem: activeActionsItem,
                regularItem: item,
                items
            })
        ];

        return checks.some((compute) => compute());
    }

    get actionsConfig() {
        const { items } = this.props;

        return {
            [TreeActionType.create]: {
                label: 'Add',
                onClick: this.setParentIdOfCreatingItem
            },
            [TreeActionType.delete]: {
                label: 'Delete',
                onClick: this.handleDeleteItem
            },
            [TreeActionType.move]: {
                label: (
                    <ActionsButton
                        withBackdrop={false}
                        triggerType={TRIGGER_TYPE.hover}
                        trigger={<React.Fragment>{I18n.t('Move')} <span className="fa fa-caret-right" /></React.Fragment>}
                        className="Tree__item__move-to-actions-btn"
                    >
                        <b className="Tree__item__move-to-actions-btn__label">{I18n.t('Move Into')}</b>
                        <Tree 
                            features={{
                                collapsing: false,
                            }}
                            className="Tree__move-management"
                            shouldDisableItem={this.shouldDisableItemOnMove}
                            items={items}
                            onChange={this.handleSubmitMoveItem} 
                        />
                    </ActionsButton>
                ),
            },
            [TreeActionType.rename]: {
                label: 'Rename',
                onClick: this.handleRenameItem
            },
        };
    }

    get features() {
        const defaultFeatures = {
            folderImg: true,
            collapsing: true,
            checkbox: false,
        };

        return { ...defaultFeatures, ...this.props.features };
    }

    handleDeleteItem = (id) => {
        const { labels } = this.props;

        this.setMessageProps({
            show: true,
            text: labels.deleteItem || 'Are you sure you want to delete an item?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onCustomOk: () => {
                this.props.onDelete(id);
                this.closeMessage();
            }
        });
    }

    handleRenameItem = (id, data) => {
        this.setRenamingItem({ id, name: data.name });
    }

    setMovingItem = (id) => {
        this.setState((state) => ({ ...state, movingItemId: id }));
    }

    resetMovingItem = () => {
        this.setState((state) => ({ ...state, movingItemId: null }));
    }

    setParentIdOfCreatingItem = (id) => {
        this.setState((state) => ({ ...state, parentIdOfCreatingItemId: id }));
    }

    resetParentIdOfCreatingItem = () => {
        this.setState((state) => ({ ...state, parentIdOfCreatingItemId: null }));
    }

    setRenamingItem = (renamingItem) => {
        this.setState((state) => ({ ...state, renamingItem }));
    }

    resetRenamingItem = () => {
        this.setState((state) => ({ ...state, renamingItem: { id: null, name: '' } }));
    }

    shouldIgnoreItemSelection = (id) => {
        return isNil(id) || (typeof id === 'string' && isEmpty(id));
    }

    onChange = (e) => {
        e.persist();

        const { id } = e.currentTarget.dataset;

        if (this.shouldIgnoreItemSelection(id)) {
            return;
        }

        const { multiple, normalizeId, onChange } = this.props;
        const normalizedId = normalizeId(id);
        const selectedItem = getItem(normalizedId, this.props.items);

        if (multiple) {
            const { selectionType } = this.props;
            const isSelectedAlready = this.getItemState(selectedItem.id).selected;
            const selectionConfig = {
                [TreeSelectionType.independent]: () => {
                    return isSelectedAlready
                        ? this.value.filter((x) => x !== selectedItem.id)
                        : [...this.value, selectedItem.id]
                },
                [TreeSelectionType.parentChild]: () => {
                    const allChildren = getAllChildren(selectedItem).map((x) => x.id);
                    const selectedNodes = [...allChildren, selectedItem.id];

                    return isSelectedAlready
                        ? this.value.filter((x) => !selectedNodes.includes(x))
                        : [...this.value, ...selectedNodes]
                },
            };

            const nextValue = selectionConfig[selectionType]();

            onChange(nextValue);

            return;
        }

        if (this.value.includes(id)) {
            return;
        }

        onChange(selectedItem.id, selectedItem);
    }

    isCollapsed = (id) => {
        return this.state.collapsedItems.some((collapsedId) => id == collapsedId);
    }

    toggleCollapseItemState = (e) => {
        e.persist();

        if (this.features.checkbox) {
            e.stopPropagation();
        }

        const { dataset: { id } } = e.target;

        this.setState(
            (state) => {
                const isCollapsed = this.isCollapsed(id);

                return {
                    ...state,
                    collapsedItems: isCollapsed
                        ? state.collapsedItems.filter((collapsedId) => collapsedId != id)
                        : [...state.collapsedItems, id],
                };
            }, 
            () => {
                const { onCollapsedItemsChange } = this.props;

                if (onCollapsedItemsChange) {
                    const { collapsedItems } = this.state;

                    onCollapsedItemsChange(collapsedItems);
                }
            }
        );
    }

    getActions = (item) => {
        const { getActions, items } = this.props;
        const actions = getActions(item, items);

        return [...actions.map((action) => ({...this.actionsConfig[action.type], ...action }))];
    }

    handleActionsOpen = (id, data) => {
        this.setState((state) => ({ ...state, activeActionsItem: data }));
    }

    handleSubmitCreateItem = async (name) => {
        const resetCreatingItemMetadata = () => {
            this.creatingItemMetadata = {
                hasErrorOnCreate: false,
                id: null,
            };
        };

        const showOnCreateError = (text) => {
            this.openErrorMessage(
                text,
                {
                    confirmText: 'Rename',
                    onCustomOk: this.closeMessage,
                    hideMessageBox: () => {
                        this.props.onCreateCancel(this.creatingItemMetadata.id);
                        resetCreatingItemMetadata();
                        this.resetParentIdOfCreatingItem();
                        this.closeMessage();
                    }
                }
            );
        };

        if (this.creatingItemMetadata.hasErrorOnCreate) {
            try {
                await this.props.onCreateRetry(this.creatingItemMetadata.id, name);
                this.resetParentIdOfCreatingItem();
                resetCreatingItemMetadata();
            } catch (err) {
                showOnCreateError(err.entity);
            }

            return;
        } 

        const { item, error } = await this.props.onCreate(this.state.parentIdOfCreatingItemId, name);

        this.creatingItemMetadata.id = item.id;

        if (error) {
            this.creatingItemMetadata.hasErrorOnCreate = true;
            showOnCreateError(error.entity);
        } else {
            this.resetParentIdOfCreatingItem();
            resetCreatingItemMetadata();
        }
    }

    handleSubmitItemName = async (name, { changed }) => {
        if (!changed) {
            this.resetRenamingItem();

            return;
        }

        try {
            await this.props.onRename(this.state.renamingItem.id, name);
        } catch (err) {
            this.openErrorMessage(err.entity);
        } finally {
            this.resetRenamingItem();
        }
    }

    handleSubmitMoveItem = async (movingToId) => {
        try {
            await this.props.onMove(this.state.activeActionsItem.id, movingToId);
        } catch (err) {
            this.openErrorMessage(err.entity);
        }
    }

    get value() {
        const { value } = this.props;

        if (Array.isArray(value)) {
            return value;
        }

        if (!this.shouldIgnoreItemSelection(value)) {
            return [value];
        }
        
        return [];
    }

    shouldDisableItem = (item) => {
        const { shouldDisableItem, items } = this.props;

        return shouldDisableItem(item, items);
    }

    getItemState = (id) => {
        return this.itemsState[id]?.getState() || { selected: false, indeterminate: false };
    }

    isRenamingName = (id) => {
        return this.state.renamingItem.id == id;
    }

    get commonTreeItemProps() {
        const { itemNotSelectedImgSrc, itemSelectedImgSrc } = this.props;
        const { parentIdOfCreatingItemId, renamingItem } = this.state;

        return {
            features: this.features,
            getActions: this.getActions,
            getId: this.props.getId,
            isCollapsed: this.isCollapsed,
            isRenamingName: this.isRenamingName,
            getState: this.getItemState,
            itemNotSelectedImgSrc,
            itemSelectedImgSrc,
            onActionsOpen: this.handleActionsOpen,
            onChange: this.onChange,
            onCollapsedChange: this.toggleCollapseItemState,
            onCreateItemSubmit: this.handleSubmitCreateItem,
            onItemNameSubmit: this.handleSubmitItemName,
            parentIdOfCreatingItemId,
            renamingItem,
            shouldDisableItem: this.shouldDisableItem,
            renderChild: this.renderChild,
        };
    }

    renderChild = (item, { parentIndex, ownIndex }) => {
        return (
            <TreeItem 
                isChild
                data={item} 
                parentIndex={parentIndex}
                ownIndex={ownIndex}
                {...this.commonTreeItemProps}
            />
        );
    }

    get itemsState() {
        const { items, selectionType } = this.props;

        const isSelected = (id) => {
            return this.value.includes(id);
        };

        const state = {};
        class Node {
            id;
            children = [];

            constructor({ id, children }) {
                this.id = id;
                this.children = (children || []).map((x) => new Node({ id: x.id, children: x.children }));
                this.children.forEach((x) => {
                    state[id] = x;
                });
                state[id] = this;
            }

            getState = () => {
                let selected = true;
                let indeterminate = false;

                if (this.children?.length && selectionType === TreeSelectionType.parentChild) {
                    this.children.forEach((x) => {
                        const state = x.getState();

                        if (!state.selected) {
                            selected = false;
                        } 
                        
                        if (state.selected || state.indeterminate) {
                            indeterminate = true;
                        }
                    });

                    return {
                        indeterminate: indeterminate && !selected,
                        selected,
                    };
                }

                selected = isSelected(this.id);

                return {
                    selected,
                };
            }
        }

        items.forEach((x) => {
            state[x.id] = new Node({ id: x.id, children: x.children });
        });

        return state;
    }

    render() {
        const { items, className, getId, collapsedItems } = this.props;
        return (
            <div className={cx('Tree', className)}>
                <MessageBox
                    hideMessageBox={this.closeMessage}
                    cancelBtnId={getId?.promptCancelBtn ? getId.promptCancelBtn() : undefined}
                    confirmBtnId={getId?.promptConfirmBtn ? getId.promptConfirmBtn() : undefined}
                    {...this.state.messageProps}
                />
                {items.map((item, index) =>
                    <TreeItem 
                        ownIndex={index}
                        getId={getId}
                        data={item}
                        key={item.id}
                        {...this.commonTreeItemProps}
                    />
                )}
            </div>
                    
        );
    }
}